import { DrawSystemImpl, System } from 'minecs';
import type { ReadOnlyGameModel } from 'yage/game/GameModel';
import { PixiViewportSystem } from 'yage/systems/render/PixiViewport';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportState, AirportPath } from '../components/index.js';
import { Locomotion } from 'yage/schemas/entity/Locomotion';
import { CONFIG } from '../domain/config.js';
import { cameraQuarter, projectWorld, projectedBounds, deadZoneCenter } from '../render/camera.js';
import { LOCAL, localPlayer } from '../services/local.js';
import { distance } from '../services/world.js';

/** World units the view leads a walking captain by (about 12% of a desktop half-view). */
const LOOK_AHEAD = 80;
@System(AirportState)
export class AirportCamera extends DrawSystemImpl<ReadOnlyGameModel> {
    static depth = 10005;
    private x = 660;
    private y = 780;
    private quarter = -1;
    run = (g: ReadOnlyGameModel) => {
        LOCAL.model = g;
        const engine = g.getSystem(PixiViewportSystem), vp = engine.viewport;
        const id = localPlayer(g);
        if (!vp || id < 0) return;
        const w = engine.pixiApp.renderer.width, h = engine.pixiApp.renderer.height;
        const scale = w < 700 ? Math.max(0.62, Math.min(0.82, w / 530)) : Math.max(0.9, Math.min(1.25, h / 760));
        if (Math.abs(vp.scale.x - scale) > 0.001) vp.setZoom(scale, true);
        const p = g.getTypedUnsafe(Transform, id);
        const shot = LOCAL.cinematic;
        if (shot && (shot.until < g.frame || shot.captain !== id || distance(p, shot.origin) > 1 || !g.hasComponent(Transform, shot.entity)))
            { LOCAL.cinematic = null; LOCAL.guidanceFocus = null; }
        if (LOCAL.cinematic) {
            const plane = g.getTypedUnsafe(Transform, LOCAL.cinematic.entity);
            LOCAL.guidanceFocus = { point: { x: plane.x, y: plane.y + 60 }, captain: id, origin: { x: p.x, y: p.y } };
        }
        const focus = LOCAL.guidanceFocus;
        // Keyboard movement also returns the view to the captain, without sending an input event.
        if (focus && (focus.captain !== id || distance(p, focus.origin) > 1))
            LOCAL.guidanceFocus = null;
        // Look ahead a little in the walking direction so the captain sees where they are heading.
        const v = g.hasComponent(Locomotion, id) ? g.getTypedUnsafe(Locomotion, id) : null;
        const walking = !LOCAL.guidanceFocus && !!v && g.hasComponent(AirportPath, id) && (v.x !== 0 || v.y !== 0);
        const lead = walking && !LOCAL.reducedMotion ? LOOK_AHEAD : 0;
        const target = projectWorld((LOCAL.guidanceFocus?.point.x ?? p.x) + (walking ? v!.directionX * lead : 0), (LOCAL.guidanceFocus?.point.y ?? p.y) + (walking ? v!.directionY * lead : 0));
        const map = projectedBounds(0, 150, CONFIG.width, CONFIG.height), halfW = w / scale / 2, halfH = h / scale / 2;
        const clamp = (v: number, lo: number, hi: number) => lo > hi ? (lo + hi) / 2 : Math.max(lo, Math.min(hi, v));
        // Follow with a dead zone (18% × 14% of the view); a GO focus still centres exactly.
        const followX = LOCAL.guidanceFocus || this.quarter !== cameraQuarter() ? target.x : deadZoneCenter(this.x, target.x, halfW * 0.18);
        const followY = LOCAL.guidanceFocus || this.quarter !== cameraQuarter() ? target.y - 115 : deadZoneCenter(this.y, target.y - 115, halfH * 0.14);
        const targetX = LOCAL.guidanceFocus ? target.x : clamp(followX, map.left + halfW, map.right - halfW);
        const targetY = LOCAL.guidanceFocus ? target.y : clamp(followY, map.top + halfH, map.bottom - halfH + 60);
        if (this.quarter !== cameraQuarter()) {
            this.quarter = cameraQuarter();
            this.x = targetX;
            this.y = targetY;
        }
        const k = LOCAL.reducedMotion ? 1 : 0.18;
        this.x += (targetX - this.x) * k;
        this.y += (targetY - this.y) * k;
        vp.moveCenter(this.x, this.y);
    };
    cleanup = () => { LOCAL.guidanceFocus = null; };
}
